import { useState } from "react"
import { FiMap } from "react-icons/fi"
import InfoBox from "../ui/InfoBox"
import SectionHeader from "../ui/SectionHeader"
import { useResetListener } from "../../hooks/useResetListener"
import StateSelectionPanel from "./StateSelectionPanel"
import { EPO_EXTENSION_STATES, EPO_VALIDATION_STATES } from "../../utils/countryData"

const CONTRACTING_STATES = [
  "AL",
  "AT",
  "BE",
  "BG",
  "CH/LI",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "ES",
  "FI",
  "FR",
  "GB",
  "GR",
  "HR",
  "HU",
  "IE",
  "IS",
  "IT",
  "LT",
  "LU",
  "LV",
  "MC",
  "MD",
  "ME",
  "MK",
  "MT",
  "NL",
  "NO",
  "PL",
  "PT",
  "RO",
  "RS",
  "SE",
  "SI",
  "SK",
  "SM",
  "TR"
]

export type DesignationStatesValue = {
  differentContractingStatesPerApplicant: boolean
  extensionStatesEnabled: boolean
  extensionStateMode: "all" | "selected"
  selectedExtensionStates: string[]
  validationStatesEnabled: boolean
  validationStateMode: "all" | "selected"
  selectedValidationStates: string[]
}

const EMPTY_DESIGNATION_STATES: DesignationStatesValue = {
  differentContractingStatesPerApplicant: false,
  extensionStatesEnabled: false,
  extensionStateMode: "all",
  selectedExtensionStates: [],
  validationStatesEnabled: false,
  validationStateMode: "all",
  selectedValidationStates: []
}

function BlueNotice({
  children,
  className = ""
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={`rounded border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800 ${className}`}
    >
      {children}
    </div>
  )
}

function Checkbox({
  checked,
  disabled,
  onChange
}: {
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) {
  return (
    <input
      type="checkbox"
      checked={checked}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      className="mt-0.5 h-5 w-5 shrink-0 rounded-sm border-gray-300 text-dantzBlue focus:ring-dantzBlue disabled:cursor-not-allowed disabled:opacity-50"
    />
  )
}

export default function DesignationStatesSection({
  value,
  onChange,
  disabled = false
}: {
  value?: DesignationStatesValue | null
  onChange?: (value: DesignationStatesValue) => void
  disabled?: boolean
}) {
  const [designationStates, setDesignationStates] =
    useState<DesignationStatesValue>(value || EMPTY_DESIGNATION_STATES)

  const updateDesignationStates = (next: DesignationStatesValue) => {
    setDesignationStates(next)
    onChange?.(next)
  }

  useResetListener(() => {
    updateDesignationStates(EMPTY_DESIGNATION_STATES)
  })

  const toggleState = (
    code: string,
    checked: boolean,
    selectedStates: string[]
  ) => {
    return checked
      ? Array.from(new Set([...selectedStates, code]))
      : selectedStates.filter((state) => state !== code)
  }

  const {
    extensionStatesEnabled,
    extensionStateMode,
    selectedExtensionStates,
    validationStatesEnabled,
    validationStateMode,
    selectedValidationStates
  } = designationStates

  const handleExtensionEnabledChange = (checked: boolean) => {
    updateDesignationStates({
      ...designationStates,
      extensionStatesEnabled: checked
    })
  }

  const handleValidationEnabledChange = (checked: boolean) => {
    updateDesignationStates({
      ...designationStates,
      validationStatesEnabled: checked
    })
  }

  const handleExtensionModeChange = (mode: "all" | "selected") => {
    updateDesignationStates({
      ...designationStates,
      extensionStateMode: mode
    })
  }

  const handleValidationModeChange = (mode: "all" | "selected") => {
    updateDesignationStates({
      ...designationStates,
      validationStateMode: mode
    })
  }

  const handleExtensionStateToggle = (code: string, checked: boolean) => {
    updateDesignationStates(
      {
        ...designationStates,
        selectedExtensionStates: toggleState(
          code,
          checked,
          selectedExtensionStates
        )
      }
    )
  }

  const handleValidationStateToggle = (code: string, checked: boolean) => {
    updateDesignationStates(
      {
        ...designationStates,
        selectedValidationStates: toggleState(
          code,
          checked,
          selectedValidationStates
        )
      }
    )
  }

  return (
    <div id="designation-states">
      <SectionHeader
        icon={<FiMap size={20} />}
        title="Designation of contracting states, extension states and validation states"
      >
        <InfoBox title="Designation of states">
          All contracting states are designated automatically. Extension and
          validation states can be selected if required.
        </InfoBox>
      </SectionHeader>

      <div className="grid gap-5 lg:grid-cols-[minmax(14rem,23rem)_minmax(0,1fr)]">
        <div className="flex flex-col gap-5">
          <label className="flex cursor-not-allowed gap-3 text-sm font-medium leading-snug text-gray-400">
            <Checkbox
              checked={false}
              disabled
              onChange={() => {}}
            />
            <span>
              Multiple applicants - if applicable, designation of different
              contracting states for individual applicants (Article 59 EPC)
            </span>
          </label>

          <label className="flex cursor-pointer gap-3 text-sm font-medium leading-snug text-gray-700">
            <Checkbox
              checked={extensionStatesEnabled}
              disabled={disabled}
              onChange={handleExtensionEnabledChange}
            />
            <span>Select designated extension states:</span>
          </label>

          <label className="flex cursor-pointer gap-3 text-sm font-medium leading-snug text-gray-700">
            <Checkbox
              checked={validationStatesEnabled}
              disabled={disabled}
              onChange={handleValidationEnabledChange}
            />
            <span>Select designated validation states:</span>
          </label>
        </div>

        <BlueNotice className="leading-relaxed">
          <p className="font-semibold">
            All contracting states are designated under Article 79(1) EPC.
          </p>
          <p className="mt-1">({CONTRACTING_STATES.join(", ")})</p>
          <p className="mt-5">
            Select the checkbox 'Multiple applicants - if applicable,
            designation of different contracting states for individual
            applicants (Article 59 EPC)' if applicable.
          </p>
        </BlueNotice>
      </div>

      {(extensionStatesEnabled || validationStatesEnabled) && (
        <div className="mt-6 flex flex-col gap-5">
          {extensionStatesEnabled && (
            <StateSelectionPanel
              groupName="patent-extension-states"
              allLabel="All states with extension agreements"
              options={EPO_EXTENSION_STATES}
              selectedStates={selectedExtensionStates}
              selectionMode={extensionStateMode}
              disabled={disabled}
              notice={
                <>
                  <span className="font-semibold">Note:</span> Extension and
                  validation fees are only considered for the states marked
                  here.
                </>
              }
              onSelectionModeChange={handleExtensionModeChange}
              onStateToggle={handleExtensionStateToggle}
            />
          )}

          {validationStatesEnabled && (
            <StateSelectionPanel
              groupName="patent-validation-states"
              allLabel="All states with validation agreements"
              options={EPO_VALIDATION_STATES}
              selectedStates={selectedValidationStates}
              selectionMode={validationStateMode}
              disabled={disabled}
              notice={
                <>
                  <span className="font-semibold">Note:</span> Extension and
                  validation fees are only considered for the states marked
                  here.
                </>
              }
              footnote="KH Cambodia: pharmaceutical products are excluded from patent protection in Cambodia until 2033."
              onSelectionModeChange={handleValidationModeChange}
              onStateToggle={handleValidationStateToggle}
            />
          )}
        </div>
      )}
    </div>
  )
}
